/**
 * Live-location sharing in chat threads.
 *
 * Like the in-call location share and session checkpoints, a live-location share
 * rides the normal message-send path as a SYSTEM message with metadata embedded
 * in the body (no GraphQL schema / resolver change). The SHARER's device posts
 * the message and then periodically EDITS it (updateMessage) with a fresh
 * position until `expiresAt` or the user stops. The display text is the
 * human-readable fallback for older clients and thread/notification previews.
 */

export const LIVE_LOCATION_META_PREFIX = '\u0000ASCEND_LIVELOC:'

export type LiveLocationMeta = {
  v: 1
  sharerId: string
  sharerName: string
  lat: number
  lng: number
  accuracy?: number
  /** ISO timestamps. */
  startedAt: string
  expiresAt: string
  updatedAt: string
  /** True once the sharer stops early (expiresAt is also clamped to now). */
  stopped?: boolean
}

export function encodeLiveLocationBody(meta: Omit<LiveLocationMeta, 'v'>): string {
  const full: LiveLocationMeta = { v: 1, ...meta }
  return `${LIVE_LOCATION_META_PREFIX}${JSON.stringify(full)}\n📍 ${meta.sharerName} is sharing live location`
}

export function parseLiveLocationBody(body: string): {
  meta: LiveLocationMeta | null
  displayText: string
} {
  if (!body.startsWith(LIVE_LOCATION_META_PREFIX)) {
    return { meta: null, displayText: body }
  }
  const newline = body.indexOf('\n')
  if (newline < 0) return { meta: null, displayText: body }
  const json = body.slice(LIVE_LOCATION_META_PREFIX.length, newline)
  try {
    const meta = JSON.parse(json) as LiveLocationMeta
    if (meta?.v !== 1) return { meta: null, displayText: body.slice(newline + 1) }
    return { meta, displayText: body.slice(newline + 1) }
  } catch {
    return { meta: null, displayText: body }
  }
}

/** Marker without the leading NUL byte (some stores strip it from snippets). */
const LIVE_LOCATION_MARKER = LIVE_LOCATION_META_PREFIX.slice(1)

/** Short thread/sidebar preview; null when `body` is not a live-location message. */
export function liveLocationThreadPreview(body: string): string | null {
  if (!body || !body.includes(LIVE_LOCATION_MARKER)) return null
  return '📍 Live location'
}

/** Whether a share is still actively broadcasting at `now`. */
export function isLiveLocationActive(meta: LiveLocationMeta, now: number = Date.now()): boolean {
  if (meta.stopped) return false
  const expires = Date.parse(meta.expiresAt)
  return Number.isFinite(expires) && expires > now
}

/** Preset durations offered in the composer (custom is handled separately). */
export const LIVE_LOCATION_DURATIONS: { label: string; ms: number }[] = [
  { label: '15 minutes', ms: 15 * 60 * 1000 },
  { label: '30 minutes', ms: 30 * 60 * 1000 },
  { label: '1 hour', ms: 60 * 60 * 1000 },
]
